import type { RouteFactory } from '@/routing/types'
import { Outlet, redirect } from 'react-router-dom'
import { TimelineAssetRouteSegments, TimelineAssetTabs } from '@/timeline/asset/constants'
import MatterRecordEventsTable from '@/matter-asset-common/events/MatterRecordEventsTable'
import TimelineAssetRoot from '@/timeline/asset/components/TimelineAssetRoot'
import TimelineAssetLayout from '@/timeline/asset/components/TimelineAssetLayout'
import AddFilesPage from '@/timeline/asset/components/add-files/AddFilesPage'
import SourceFilesTab from '@/timeline/asset/components/source-files/SourceFilesTab'
import MatterFeatureGuard from '@/components/guards/MatterFeatureGuard'
import { canEditTimeline } from '@/matter/utils/matter-permissions'
import { matterPaths } from '@/routing/paths'

/**
 * Creates timeline routes for nesting under MatterLayout.
 * URL pattern: /dashboard/m/:matterId/timeline/...
 *
 * The living timeline uses user_file ids for every ingestion path.
 *
 * The index is the matter's living timeline (one per matter). The legacy job viewer is gone
 * (hard cutover); old job deep links land on the living timeline.
 *
 * @see MatterLayout for parent route context
 */
export const createTimelineChildRoutes: RouteFactory = () => [
  {
    path: 'timeline',
    children: [
      {
        element: (
          <TimelineAssetRoot>
            <Outlet />
          </TimelineAssetRoot>
        ),
        children: [
          // The living timeline (tabbed shell). Timeline has a single events view, so the All Events
          // tab mounts the shared MatterRecordEventsTable directly (no variant); columns come from
          // timelineFeatureConfig via the shell's MatterAssetFeatureProvider.
          {
            element: <TimelineAssetLayout />,
            children: [
              { index: true, loader: () => redirect(TimelineAssetTabs.ALL_EVENTS) },
              { path: TimelineAssetTabs.ALL_EVENTS, element: <MatterRecordEventsTable /> },
              { path: TimelineAssetTabs.SOURCE_FILES, element: <SourceFilesTab /> },
            ],
          },
          // Stage files and generate/update the timeline. Editing is owner-only, so a view-only
          // collaborator who deep-links here is redirected back to the timeline they can see.
          {
            element: <MatterFeatureGuard canAccess={canEditTimeline} redirectTo={matterPaths.timeline.index} />,
            children: [{ path: TimelineAssetRouteSegments.ADD_FILES, element: <AddFilesPage /> }],
          },
          // Hard cutover: legacy job deep links land on the living timeline
          { path: '*', loader: () => redirect('..') },
        ],
      },
    ],
  },
]
